import { addDays, isSameDay, isToday } from "date-fns"

export class Time {
    #value: number
    get hour(): number {
        return Math.floor(this.#value / 3600)
    }
    get minute(): number {
        return Math.floor(this.#value / 60) % 60
    }
    get second(): number {
        return this.#value % 60
    }
    set hour(value: number) {
        this.#value += (value - this.hour) * 3600
        if( this.#value < 0 ) {
            this.#value = 0
        }
    }
    set minute(value: number) {
        this.#value += (value - this.minute) * 60
        if( this.#value < 0 ) {
            this.#value = 0
        }
    }
    set second(value: number) {
        this.#value += (value - this.second)
        if( this.#value < 0 ) {
            this.#value = 0
        }
    }

    constructor(value: undefined | string | number | Date | Time = undefined) {
        this.#value = 0
        if (value === undefined) {
            value = new Date()
        }
        if (typeof value === "string") {
            let value_data = value.match(/^([0-9]*):([0-9]*):?([0-9]*)$/)
            if (value_data) {
                this.hour = parseInt(value_data[1])
                this.minute = parseInt(value_data[2])
                this.second = value_data[3] ? parseInt(value_data[3]) : 0
            }
        }
        if (typeof value === "number") {
            this.second = value % 60
            this.minute = Math.floor(value / 60) % 60
            this.hour = Math.floor(value / 3600)
        }
        if (value instanceof Date) {
            this.second = value.getSeconds()
            this.minute = value.getMinutes()
            this.hour = value.getHours()
        }
        if (value instanceof Time) {
            this.second = value.second
            this.minute = value.minute
            this.hour = value.hour
        }
    }

    add(value: Time | number) : Time|null{
        let cur = this.toSec()
        if (value instanceof Time) {
            return new Time(cur + value.toSec())
        }
        if (typeof value === 'number') {
            return new Time(cur + value)
        }
        return null
    }

    sub(value: Time | number) : Time|null{
        let cur = this.toSec()
        if (value instanceof Time) {
            return new Time(cur - value.toSec())
        }
        if (typeof value === 'number') {
            return new Time(cur - value)
        }
        return null
    }

    isBefore(value: Time | number, orEqual: boolean = false):boolean |null {
        let cur = this.toSec()
        if (value instanceof Time) {
            return orEqual ? cur <= value.toSec() : cur < value.toSec()
        }
        if (typeof value === 'number') {
            return orEqual ? cur <= value : cur < value
        }
        return null
    }

    isAfter(value: Time | number, orEqual: boolean = false):boolean |null {
        let cur = this.toSec()
        if (value instanceof Time) {
            return orEqual ? cur >= value.toSec() : cur > value.toSec()
        }
        if (typeof value === 'number') {
            return orEqual ? cur >= value : cur > value
        }
        return null
    }

    isBetween(value1: undefined | string | number | Date | Time, value2: undefined | string | number | Date | Time, orEqual: boolean = false):boolean |null {
        let cur = this.toSec()
        if (value1 instanceof Time) {
            value1 = value1.toSec()
        } else {
            value1 = new Time(value1)
        }
        if (value2 instanceof Time) {
            value2 = value2.toSec()
        } else {
            value2 = new Time(value2)
        }
        if (value2 < value1) {
            let tmp = value2
            value2 = value1
            value1 = tmp
        }
        return orEqual ? value1 <= cur && cur <= value2 : value1 < cur && cur < value2
    }

    /**
     * format
     */
    format(format: string = "H:mm:ss"): string {
        format = format.replaceAll("HH", ("0" + this.hour).slice(-2))
        format = format.replaceAll("mm", ("0" + this.minute).slice(-2))
        format = format.replaceAll("ss", ("0" + this.second).slice(-2))
        format = format.replaceAll("H", `${this.hour}`)
        format = format.replaceAll("m", `${this.minute}`)
        format = format.replaceAll("s", `${this.second}`)
        return format
    }

    /**
     * 文字列変換
     * @returns {string} H:mm:ss
     */
    toString(): string {
        return this.hour + ":" + ("0" + this.minute).slice(-2) + ":" + ("0" + this.second).slice(-2)
    }

    /**
     * 文字列変換
     * @returns {string} H:mm
     */
    toStringHM(): string {
        return this.hour + ":" + ("0" + this.minute).slice(-2)
    }

    /**
     * 文字列変換
     * @returns {string} H:mm
     */
    toStringHMJP(): string {
        return this.hour + "時" + ("0" + this.minute).slice(-2) + "分"
    }

    /**
     * 時間間隔文字列変換
     * @returns string H時間mm分ss秒
     */
    toStgingLength(): string {
        let sz = this.hour + "時間"
        if( this.minute>0 || this.second>0 ) {
            sz = sz + ("0" + this.minute).slice(-2) + "分"
        }
        if( this.second>0 ) {
            sz = sz + ("0" + this.second).slice(-2) + "秒"
        }
        return sz
    }

    /**
     * 秒数に変換
     * @returns {number} sec
     */
    toSec(): number {
        return this.hour * 3600 + this.minute * 60 + this.second
    }

    *eachUnit(end: Time, unit: Time) {
        let e = end.toSec()
        let u = unit.toSec()
        for (let cur = this.toSec(); cur < e; cur += u) {
            yield new Time(cur)
        }
    }
}

export function nowWorkTime(workingTime: { open: any, close: any }) {
    let time = new Time()
    let workEnd = new Time(workingTime.close)
    if (workEnd.hour >= 24) {
        // 24時を超える場合
        let time2 = time.add(24 * 60 * 60)
        if (time2 && time2.isBefore(workEnd, true)) {
            return time2
        }
    }
    return time
}

export function isWorkToday(date: string | Date, workingTime: { open: any, close: any }) {
    if (typeof date === 'string') {
        date = new Date(date)
    }

    let dateOfTime = new Time(date)
    let workBegin = new Time(workingTime.open)
    let workEnd = new Time(workingTime.close)

    if (dateOfTime.isBefore(workBegin)) {
        date = addDays(date, -1)
    }
    if (dateOfTime.isAfter(workEnd)) {
        date = addDays(date, 1)
    }

    return isToday(date)
}

export function isWorkDay(datetime: string | Date, date: string | Date, workingTime: { open: any, close: any }) {
    if (typeof datetime === 'string') {
        datetime = new Date(datetime)
    }
    if (typeof date === 'string') {
        date = new Date(date)
    }

    let dateOfTime = new Time(datetime)
    let workBegin = new Time(workingTime.open)
    let workEnd = new Time(workingTime.close)

    if (dateOfTime.isBefore(workBegin)) {
        datetime = addDays(datetime, -1)
    }
    if (dateOfTime.isAfter(workEnd)) {
        datetime = addDays(datetime, 1)
    }

    return isSameDay(datetime, date)
}

export function minTime(...times: Time[]) {
    let minSec: number | undefined = undefined
    times.forEach(t => {
        minSec = minSec !== undefined ? Math.min(t.toSec(), minSec) : t.toSec()
    });
    return new Time(minSec)
}

export function maxTime(...times: Time[]) {
    let maxSec = 0
    times.forEach(t => {
        maxSec = Math.max(t.toSec(), maxSec)
    });
    return new Time(maxSec)
}

export function isEqual(time1: undefined | string | number | Date | Time, time2: undefined | string | number | Date | Time) {
    let t1 = new Time(time1)
    let t2 = new Time(time2)
    return t1.toSec() === t2.toSec()
}

export function timeLength(time1: undefined | string | number | Date | Time, time2: undefined | string | number | Date | Time): Time | null {
    let t1 = new Time(time1)
    let t2 = new Time(time2)
    return t1.isAfter(t2) ? t1.sub(t2) : t2.sub(t1)
}

export function timeLengthString(time1: undefined | string | number | Date | Time, time2: undefined | string | number | Date | Time): string {
    let len = timeLength(time1, time2)
    return len ? len.toStgingLength() : ""
}

export function secToTimeString(sec: number) : string {
    let t = new Time(sec)
    return t.toString()
}

export function secToTimeStringHM(sec: number) : string {
    let t = new Time(sec)
    return t.toStringHM()
}

// Time.prototype.toString = function(){
//     return this.hour + ":" + ("0"+this.minute).slice(-2) + ":" + ("0"+this.second).slice(-2)
// }